import { apiService } from './apiService'
import { API_ENDPOINTS } from '@/config/api'
import type {
  OtpRequest,
  OtpResponse,
  OtpVerifyRequest,
  AuthResponse,
  RefreshTokenRequest,
  User
} from '@/types/auth'

const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === 'true' || false
// Authentication may wake a paused Azure API/SQL instance. Do not abort a valid
// sign-in while the server is warming; LoginView provides progressive feedback.
export const AUTH_REQUEST_TIMEOUT_MS = 0

export const authService = {
  // Request OTP
  async requestOtp(identifier: string, type: 'email' = 'email'): Promise<OtpResponse> {
    if (USE_MOCK_API) {
      console.log('Mock API: Requesting OTP for', identifier)
      await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate network delay
      return {
        message: 'OTP sent successfully',
        expiresIn: 300
      }
    }

    const response = await apiService.post<OtpResponse>(API_ENDPOINTS.AUTH.REQUEST_OTP, {
      identifier,
      type
    }, { timeout: AUTH_REQUEST_TIMEOUT_MS })
    return response
  },

  // Verify OTP and authenticate
  async verifyOtp(identifier: string, otp: string, userData?: any): Promise<AuthResponse> {
    if (USE_MOCK_API) {
      console.log('Mock API: Verifying OTP for', identifier)
      await new Promise(resolve => setTimeout(resolve, 1500)) // Simulate network delay

      // Mock successful verification
      const mockUser: User = {
        id: 1,
        email: identifier.includes('@') ? identifier : '',
        mobile: identifier.includes('@') ? '' : identifier,
        firstName: userData?.firstName || 'Demo',
        lastName: userData?.lastName || 'User',
        role: userData?.role || 'Admin',
        isActive: true,
        isEmailVerified: true,
        isMobileVerified: true,
        createdAt: new Date().toISOString()
      }

      return {
        user: mockUser,
        accessToken: 'mock-jwt-token-' + Date.now(),
        refreshToken: 'mock-refresh-token-' + Date.now(),
        expiresIn: 3600
      }
    }

    const payload: OtpVerifyRequest = {
      identifier,
      otp,
      ...userData
    }

    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.VERIFY_OTP, payload, { timeout: AUTH_REQUEST_TIMEOUT_MS })
    return response
  },

  // Password login for admin email or provisioned parent/teacher mobile
  async loginWithPassword(identifier: string, password: string): Promise<AuthResponse> {
    const trimmedIdentifier = identifier.trim()
    const payload = trimmedIdentifier.includes('@')
      ? { email: trimmedIdentifier, identifier: trimmedIdentifier, password }
      : { mobile: trimmedIdentifier, identifier: trimmedIdentifier, password }

    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.LOGIN, {
      ...payload
    }, { timeout: AUTH_REQUEST_TIMEOUT_MS })
    return response
  },

  // Quick login for demo accounts
  async quickLogin(email: string): Promise<AuthResponse> {
    const response = await apiService.post<AuthResponse>('/auth/quick-login', {
      email
    })
    return response
  },

  // Set admin password
  async setPassword(password: string, confirmPassword: string): Promise<any> {
    const response = await apiService.post('/auth/set-password', {
      password,
      confirmPassword
    })
    return response
  },

  async validateInvitation(token: string): Promise<any> {
    return apiService.get(`/auth/invitations/${encodeURIComponent(token)}`)
  },

  async acceptInvitation(token: string, email: string, password: string): Promise<any> {
    return apiService.post('/auth/invitations/accept', { token, email, password })
  },

  async getAccountSecurity(): Promise<any> {
    return apiService.get('/account/security')
  },

  async updateRecoveryEmail(email: string): Promise<any> {
    return apiService.put('/account/security/email', { email })
  },

  async requestAccountDeletion(reason = ''): Promise<any> {
    return apiService.post('/account/deletion-request', { reason })
  },

  async requestPublicAccountDeletion(emailOrMobile: string, reason = ''): Promise<any> {
    return apiService.post('/account/public-deletion-request', { emailOrMobile, reason })
  },

  // Refresh access token
  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    if (USE_MOCK_API) {
      console.log('Mock API: Refreshing token')
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        user: {} as User, // Would be populated in real implementation
        accessToken: 'mock-refreshed-jwt-token-' + Date.now(),
        refreshToken: 'mock-new-refresh-token-' + Date.now(),
        expiresIn: 3600
      }
    }

    const response = await apiService.post<AuthResponse>(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
      refreshToken
    } as RefreshTokenRequest, { timeout: AUTH_REQUEST_TIMEOUT_MS })
    const payload = (response as any)?.accessToken ? response : ((response as any)?.data || response)
    return {
      ...payload,
      accessToken: payload.accessToken || payload.AccessToken,
      refreshToken: payload.refreshToken || payload.RefreshToken,
      user: payload.user || payload.User
    }
  },

  // Logout
  async logout(): Promise<void> {
    if (USE_MOCK_API) {
      console.log('Mock API: Logging out')
      await new Promise(resolve => setTimeout(resolve, 300))
      return
    }

    await apiService.post(API_ENDPOINTS.AUTH.LOGOUT)
  },

  // Get current user
  async getCurrentUser(): Promise<User> {
    if (USE_MOCK_API) {
      console.log('Mock API: Getting current user')
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        id: 1,
        email: 'demo@mastermind.com',
        mobile: '',
        firstName: 'Demo',
        lastName: 'User',
        role: 'Admin',
        isActive: true,
        isEmailVerified: true,
        isMobileVerified: true,
        createdAt: new Date().toISOString()
      }
    }

    const response = await apiService.get<User>(API_ENDPOINTS.AUTH.CURRENT_USER)
    return response
  },

  // Check authentication status
  async checkAuth(): Promise<boolean> {
    if (USE_MOCK_API) {
      console.log('Mock API: Checking auth status')
      await new Promise(resolve => setTimeout(resolve, 300))
      return true
    }

    try {
      await this.getCurrentUser()
      return true
    } catch {
      return false
    }
  }
}

export default authService
